import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import { ErrorState } from '../../components/ui/ErrorState';
import { PageHeader } from '../../components/ui/PageHeader';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { apiClient } from '../../lib/api-client';
import { useAuth } from '../auth/auth-context';

import { CreateOrganizationModal } from './CreateOrganizationModal';
import { OrganizationDisplayModal } from './OrganizationDisplayModal';

interface Org {
  id: string;
  name: string;
  slug: string;
  type: 'ADWEB' | 'CLIENT';
  status: 'ACTIVE' | 'SUSPENDED';
  primaryContactEmail: string | null;
  createdAt: string;
}

interface ListResponse {
  data: Org[];
  meta: { total: number };
}

async function fetchOrganizations(): Promise<ListResponse> {
  return (await apiClient.get<ListResponse>('/organizations?page=1&pageSize=100')).data;
}

export function OrganizationsAdminPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [displayOrg, setDisplayOrg] = useState<Org | null>(null);
  const query = useQuery({ queryKey: ['organizations'], queryFn: fetchOrganizations });

  const canCreate = user?.role === 'SUPER_ADMIN';
  const canSuspend = user?.role === 'SUPER_ADMIN';
  const canDelete = user?.role === 'SUPER_ADMIN';
  const canSetDisplay = user?.role === 'SUPER_ADMIN' || user?.role === 'ADWEB_OPERATOR';
  const canExport =
    user?.role === 'SUPER_ADMIN' ||
    user?.role === 'ADWEB_OPERATOR' ||
    user?.role === 'CLIENT_ADMIN';

  const suspend = useMutation({
    mutationFn: ({ id, suspended }: { id: string; suspended: boolean }) =>
      apiClient.post(`/organizations/${id}/suspend`, { suspended }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['organizations'] }),
  });

  const exportData = useMutation({
    mutationFn: async (o: Org) => {
      const res = await apiClient.get(`/organizations/${o.id}/export`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `adweb-export-${o.slug}-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/organizations/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['organizations'] }),
  });

  function toggleSuspend(o: Org) {
    const suspended = o.status !== 'SUSPENDED';
    const verb = suspended ? 'suspendre' : 'réactiver';
    if (window.confirm(`Voulez-vous ${verb} l'organisation « ${o.name} » ?`)) {
      suspend.mutate({ id: o.id, suspended });
    }
  }

  function handleDelete(o: Org) {
    const confirmation = window.prompt(
      `Suppression DÉFINITIVE de l'organisation « ${o.name} ».\n\n` +
        'Cela efface :\n' +
        '  – tous les utilisateurs et leurs accès,\n' +
        '  – toutes les automatisations, exécutions et incidents,\n' +
        "  – tous les rapports, KPIs et journaux d'audit,\n" +
        "  – l'organisation elle-même.\n\n" +
        "Cette action est IRRÉVERSIBLE (droit à l'oubli, RGPD art. 17).\n" +
        `Pour confirmer, tapez exactement le slug : ${o.slug}`,
    );
    if (confirmation === o.slug) {
      remove.mutate(o.id);
    } else if (confirmation !== null) {
      window.alert('Le slug saisi ne correspond pas. Suppression annulée.');
    }
  }

  return (
    <>
      <PageHeader
        helpTopic="organisations"
        title={t('organizations.title')}
        subtitle={t('organizations.subtitle')}
        actions={
          canCreate ? (
            <Button variant="inverse" onClick={() => setModalOpen(true)} size="sm">
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              Créer une organisation
            </Button>
          ) : null
        }
      />
      <CreateOrganizationModal open={modalOpen} onClose={() => setModalOpen(false)} />
      <OrganizationDisplayModal org={displayOrg} onClose={() => setDisplayOrg(null)} />
      <section className="px-4 sm:px-6 lg:px-8 py-6">
        {query.isLoading ? (
          <SkeletonTable rows={4} cols={5} />
        ) : query.isError ? (
          <ErrorState onRetry={() => void query.refetch()} />
        ) : query.data && query.data.data.length === 0 ? (
          <EmptyState
            icon={Building2}
            title={t('organizations.empty')}
            action={
              canCreate ? (
                <Button onClick={() => setModalOpen(true)} size="sm" className="mt-2">
                  <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                  Créer une organisation
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="overflow-x-auto surface">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th scope="col" className="px-4 py-3">
                    {t('organizations.name')}
                  </th>
                  <th scope="col" className="hidden px-4 py-3 md:table-cell">
                    {t('organizations.slug')}
                  </th>
                  <th scope="col" className="hidden px-4 py-3 sm:table-cell">
                    {t('organizations.type')}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t('organizations.status')}
                  </th>
                  <th scope="col" className="hidden px-4 py-3 lg:table-cell">
                    {t('organizations.primaryContact')}
                  </th>
                  <th scope="col" className="px-4 py-3" aria-label="Actions"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(query.data?.data ?? []).map((o) => (
                  <tr key={o.id}>
                    <td className="px-4 py-2.5 text-slate-900">{o.name}</td>
                    <td className="hidden px-4 py-2.5 font-mono text-xs text-slate-500 md:table-cell">
                      {o.slug}
                    </td>
                    <td className="hidden px-4 py-2.5 sm:table-cell">
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">{o.type}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusBadge status={o.status === 'ACTIVE' ? 'ACTIVE' : 'PAUSED'} />
                    </td>
                    <td className="hidden px-4 py-2.5 text-slate-600 lg:table-cell">
                      {o.primaryContactEmail ?? '—'}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
                        {canSetDisplay && (
                          <button
                            type="button"
                            onClick={() => setDisplayOrg(o)}
                            className="text-xs font-medium text-brand hover:underline"
                            title="Style et présentation par défaut pour cette organisation"
                          >
                            Affichage
                          </button>
                        )}
                        {canExport && (
                          <button
                            type="button"
                            onClick={() => exportData.mutate(o)}
                            disabled={exportData.isPending}
                            className="text-xs font-medium text-brand hover:underline disabled:opacity-50"
                            title="Export RGPD (droit à la portabilité)"
                          >
                            {exportData.isPending ? 'Export…' : 'Exporter'}
                          </button>
                        )}
                        {canSuspend && o.type === 'CLIENT' && (
                          <button
                            type="button"
                            onClick={() => toggleSuspend(o)}
                            disabled={suspend.isPending}
                            className={`text-xs font-medium hover:underline disabled:opacity-50 ${
                              o.status === 'SUSPENDED' ? 'text-emerald-600' : 'text-amber-600'
                            }`}
                          >
                            {o.status === 'SUSPENDED' ? 'Réactiver' : 'Suspendre'}
                          </button>
                        )}
                        {canDelete && o.type === 'CLIENT' && (
                          <button
                            type="button"
                            onClick={() => handleDelete(o)}
                            disabled={remove.isPending}
                            className="text-xs font-medium text-red-600 hover:underline disabled:opacity-50"
                            title="Suppression définitive (droit à l'oubli)"
                          >
                            Supprimer
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
